/* eslint-disable no-alert */
/* eslint-disable max-lines-per-function */
import React, { CSSProperties, MouseEventHandler } from "react";
import { useMutation, ApolloError, QueryResult } from "@apollo/client";
import { Link } from "react-router-dom";
import MaterialTable from "@material-table/core";
import CircularProgress from "@mui/material/CircularProgress";
import InfoIcon from "@mui/icons-material/Info";
import Button from "@mui/material/Button";
import { Tooltip } from "@mui/material";
import {
  adminSearchInterests,
  adminSearchInterestsVariables,
  adminSearchInterests_adminSearchInterests as adminSearchInterestsResult,
  AdminUpdateInterest,
  AdminUpdateInterestVariables,
} from "types/gql-types";
import { CopyButton } from "components/haven-design/copy-button";
import { useSnackbar } from "components/haven-design/snackbar";
import {
  UPDATE_INTEREST,
} from "./queries";
import styles from "./result-table.module.css";

enum MutationErrors {
  InvalidUserId = "user_id_not_found",
  InvalidPhone = "invalid_phone_number",
  InvalidEmail = "invalid_email",
  LoginPhoneTaken = "login_phone_taken",
  LoginEmailTaken = "login_email_taken",
}

type RowType = JSX.IntrinsicAttributes & adminSearchInterestsResult & RowFunctions;

type RowFunctions = {
  promptLinkApp: MouseEventHandler<HTMLButtonElement>;
  correctSubmittedAt: MouseEventHandler<HTMLButtonElement>;
}

const WaitlistUpdateCell = (row : RowType) => {
  const linkHref = `/update/${row.waitlistUpdateCode}`;
  return (
    <>
      {row.waitlistUpdateResponded ? "Updated" : "Not Updated"}
      <br />
      <a href={linkHref} target="_blank" rel="noreferrer">{row.waitlistUpdateCode}</a>
    </>
  );
};

const getResultsColumns = ({ email }: adminSearchInterestsVariables) => [
  { title: "Organization / Waitlist",
    field: "organizationName",
    render (row: RowType) {
      return (<>
        <strong>{row.organizationName}</strong>
        <br />
        {row.waitlistName}
      </>);
    },
    customFilterAndSearch: (filterText : string, row: RowType) => {
      const orgName = row.organizationName || "";
      return orgName.toLowerCase().includes(filterText.toLowerCase());
    } },
  { title: "Name / Email Addresses",
    field: "name",
    render (row: RowType) {
      return (<>
        <strong>{ row.firstName } { row.lastName }</strong>
        {
          (row.householdMember0Email) && (
            <div>
              <i className="fa fa-home" title="household" /> {row.householdMember0Email}
            </div>
          )
        }
        {
          (row.applicantPrimaryPhone) && (
            <div>
              <i className="fa fa-phone" title="primary phone" /> P {row.applicantPrimaryPhone}
              <small>{row.applicantPrimaryPhoneType &&
              ` (${row.applicantPrimaryPhoneType})`}</small>
            </div>
          )
        }
        {
          (row.applicantSecondaryPhone) && (
            <div>
              <i className="fa fa-phone" title="primary phone" /> S {row.applicantPrimaryPhone}
              <small>{row.applicantSecondaryPhoneType &&
              ` (${row.applicantSecondaryPhoneType})`}</small>
            </div>
          )
        }
      </>);
    } },
  { title: "User Account",
    field: "hasAccount",
    render (row: RowType) {
      if (!row.userId) {
        return (
          <div className={styles.inlineFlex}>
            Link code: <strong>{row.linkCode}</strong><br/>
            <Button size="small" onClick={row.promptLinkApp}>Manually link to user ID</Button>
          </div>
        );
      }

      return (
        <div>
          <div className={styles.inlineFlex}>
            <Link to={`/dash/admin/users/${row.userId}`}>
              <strong>{ row.userId }</strong>
            </Link>

            <CopyButton text={ row.userId } />
          </div>

          <div className={styles.indent}>
            {row.loginPhone || "No Account Phone"}
          </div>

          <div className={styles.indent}>
            {row.loginEmail || "No Account Email"}
          </div>

          <Button size="small" onClick={row.promptLinkApp}>Move to another user ID</Button>
        </div>
      );
    } },
  { title: "Applied At",
    field: "submittedAt",
    render (row: RowType) {
      return (
        <>
          <div>{row.submittedAt}</div>
          <div><Button size="small" onClick={row.correctSubmittedAt}>Correct</Button></div>
        </>
      );
    } },
  { title: "Application Status", field: "waitlistStatus" },
  { title: "Waitlist Update",
    field: "waitlist",
    render (row: RowType) {
      return <WaitlistUpdateCell {...row} />;
    } },
  { title: "Updated At", field: "waitlistUpdateRespondedAt" },
  { title: "Contact",
    field: "contactName",
    render (row: RowType) {
      const isSearchedEmailContact = row.contactEmail?.toLowerCase() === email?.toLowerCase();
      return (<>
        {row.contactName && <div className={styles.indent}>{row.contactName}</div>}
        {row.contactEmail && <div className={styles.indent}>{isSearchedEmailContact && (
          <Tooltip title="Search email matched only alternate contact">
            <InfoIcon />
          </Tooltip>
        )}{row.contactEmail}</div>}
      </>);
    } },

  { title: "Mailing Address",
    field: "mailingAddress1",
    render: (row: RowType) => (
      <>
        <div>{row.mailingAddress1} {row.mailingSteNumber}</div>
        <div>{row.mailingCity} {row.mailingState}{row.mailingState && ","} {row.mailingZip}</div>
      </>
    ) },

  { title: "Residence Address",
    field: "residenceAddress1",
    render: (row: RowType) => (
      <>
        <div>{row.residenceAddress1} {row.residenceSteNumber}</div>
        <div>{row.residenceCity} {row.residenceState}{row.residenceState && ","} {row.residenceZip}</div>
      </>
    ) },

  { title: "Interest ID",
    field: "id",
    render (row: RowType) {
      return <>
        <strong>{ row.id.substr(30) }</strong>
        <CopyButton text={ row.id } />
      </>;
    } },
].map((column) => ({
  ...column,
  cellStyle: { padding: "0.5em 1em", whiteSpace: "nowrap" } as CSSProperties,
  headerStyle: { whiteSpace: "nowrap" } as CSSProperties,
}));

const formatApolloError = (e: ApolloError) => {
  switch (e.message) {
    case MutationErrors.InvalidUserId:
      return "Invalid user ID provided.";
    case MutationErrors.InvalidPhone:
      return "Invalid phone number/formatting";
    case MutationErrors.InvalidEmail:
      return "Invalid email address";
    case MutationErrors.LoginPhoneTaken:
      return "Phone number is already used by another user.";
    case MutationErrors.LoginEmailTaken:
      return "Email is already used by another user.";
    default:
      return `An unknown error occured - ${e.message}`;
  }
};
type ResultTableProps = {
  searchQueryData: QueryResult<adminSearchInterests, adminSearchInterestsVariables>;
  email?: string | null;
}

const ResultTable: React.FC<ResultTableProps> =
({ searchQueryData: { loading, data, error }, email }) => {
  const { openSnackbar } = useSnackbar();

  const [updateInterestMutation] = useMutation<AdminUpdateInterest,
  AdminUpdateInterestVariables>(UPDATE_INTEREST, {
    refetchQueries: ["adminSearchInterests"],
  });

  const promptLinkApp = (id: string): RowType["promptLinkApp"] => async (event) => {
    event.preventDefault();

    const userId = prompt("Enter the User ID to link to this application")?.trim();
    if (!userId) return;

    try {
      await updateInterestMutation({ variables: { id, userId } });

      openSnackbar({ message: `Interest successfully transfered to user ${userId}`, status: "success" });
    } catch (e) {
      if (e instanceof ApolloError) {
        const message = formatApolloError(e);
        openSnackbar({ message, status: "error" });

        return;
      }

      openSnackbar({ message: "Unknown error linking application", status: "error" });
    }
  };

  const correctSubmittedAt = (id: string): RowType["correctSubmittedAt"] => async (event) => {
    event.preventDefault();
    const submittedAt = prompt("Enter a new submitted at in the format: YYYY-MM-DD")?.trim();
    if (!submittedAt) return;
    try {
      await updateInterestMutation({ variables: { id, submittedAt } });
      openSnackbar({ message: `Interest's applied at date successfully updated to  ${submittedAt}`, status: "success" });
    } catch (e) {
      if (e instanceof ApolloError) {
        const message = formatApolloError(e);
        openSnackbar({ message, status: "error" });
        return;
      }
      openSnackbar({ message: "Unknown error linking application", status: "error" });
    }
  };

  if (loading) return <CircularProgress size={ 25 } />;

  if (error || !data) {
    return <div>Error loading data</div>;
  }

  const results = data.adminSearchInterests?.map(item => ({
    ...item,
    promptLinkApp: promptLinkApp(item.id),
    correctSubmittedAt: correctSubmittedAt(item.id),
  })) || [];

  if (!results.length) return <div>No Results</div>;

  const resultsColumns = getResultsColumns({ email });

  return <MaterialTable
    columns={resultsColumns}
    data={results}
    options={{
      columnsButton: true,
      paging: false,
      showTitle: false,
      toolbar: false,
    }}
  />;
};

export default ResultTable;
